(() => {
  'use strict';
  // Versioned filename prevents Shopify CDN from serving stale sidebar behavior.

  const root = document.documentElement;
  root.classList.add('levyo-maker-fullscreen-page');

  const headerSelectors = [
    '.section-header .header-wrapper',
    '.section-header .header',
    '.section-header header',
    '.section-header',
  ];
  const findHeader = () => {
    for (const selector of headerSelectors) {
      const candidate = document.querySelector(selector);
      if (candidate) return candidate;
    }
    return null;
  };
  const sectionHeader = document.querySelector('.section-header');
  const header = findHeader();
  let headerMeasureFrame = null;
  const syncHeaderHeight = () => {
    headerMeasureFrame = null;
    const measuredHeight = Number(findHeader()?.getBoundingClientRect?.().height);
    const height = Number.isFinite(measuredHeight) && measuredHeight > 0
      ? Math.ceil(measuredHeight)
      : 72;
    root.style.setProperty('--levyo-maker-site-header-height', `${height}px`);
  };
  const scheduleHeaderHeightSync = () => {
    if (headerMeasureFrame !== null) return;
    if (typeof window.requestAnimationFrame === 'function') {
      headerMeasureFrame = window.requestAnimationFrame(syncHeaderHeight);
      return;
    }
    syncHeaderHeight();
  };

  syncHeaderHeight();
  scheduleHeaderHeightSync();
  window.addEventListener('resize', scheduleHeaderHeightSync, { passive: true });
  let headerResizeObserver = null;
  if (typeof ResizeObserver === 'function' && (header || sectionHeader)) {
    headerResizeObserver = new ResizeObserver(scheduleHeaderHeightSync);
    [header, sectionHeader].filter((candidate, index, candidates) => (
      candidate && candidates.indexOf(candidate) === index
    )).forEach((candidate) => headerResizeObserver.observe(candidate));
  }
  window.addEventListener('pagehide', () => {
    if (headerMeasureFrame !== null && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(headerMeasureFrame);
      headerMeasureFrame = null;
    }
    headerResizeObserver?.disconnect?.();
  }, { once: true });

  const config = window.LevyoMakerFullscreenConfig || {};
  const icons = config.icons || {};
  const initialRequestedView = (() => {
    try {
      return new URL(window.location.href).searchParams.get('view') || '';
    } catch (_error) {
      return '';
    }
  })();
  const initialViewMaxAttempts = 20;
  let initialViewAttemptStarted = false;
  let pendingSidebarToggle = false;
  let activeSidebarBinding = null;
  let coldGuestSidebar = null;
  let coldRuntimeRequested = false;
  let coldRuntimeErrorListener = null;

  const currentMaker = () => document.querySelector('#threadly-ai-panel.threadly-ai-maker');

  const requestMakerRuntime = () => {
    if (window.THREADLY_AI_MAKER_RUNTIME_LOADED) return true;
    if (typeof window.levyoMakerBoot === 'function') {
      try {
        window.levyoMakerBoot(true);
        coldRuntimeRequested = true;
        return true;
      } catch (_error) {}
    }
    if (coldRuntimeRequested) return false;
    const loader = document.querySelector('script[src*="threadly-ai-maker.js"]');
    if (!loader?.src) return false;
    const retry = document.createElement('script');
    retry.src = loader.src;
    retry.async = true;
    retry.dataset.threadlyMakerLoaderRetry = 'guest-sidebar-first-paint';
    document.head?.appendChild(retry);
    coldRuntimeRequested = true;
    return true;
  };

  const createColdSidebarAction = (label, attribute) => {
    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'threadly-ai-sidebar-action threadly-ai-sidebar-action--plain';
    action.setAttribute(attribute, '');
    action.setAttribute('data-threadly-cold-action', '');
    action.setAttribute('aria-disabled', 'true');
    action.disabled = true;
    const text = document.createElement('span');
    text.textContent = label;
    action.append(text);
    return action;
  };

  const createColdGuestSignInControl = () => {
    const config = window.THREADLY_AI_MAKER_CONFIG || {};
    if (String(config.shopifyAccountSheetMarkup || '').trim()) {
      const account = document.createElement('shopify-account');
      account.className = 'threadly-ai-profile-login';
      account.setAttribute('data-threadly-shopify-account', 'maker-cold-sidebar');
      account.setAttribute('menu', 'customer-account-main-menu');
      account.setAttribute('sign-in-url', String(config.loginUrl || ''));
      const label = document.createElement('span');
      label.setAttribute('slot', 'signed-out-avatar');
      label.textContent = 'Sign in';
      account.append(label);
      return account;
    }

    const login = document.createElement('a');
    login.className = 'threadly-ai-profile-login';
    login.setAttribute('data-threadly-login-bypass', 'true');
    login.setAttribute('href', String(config.loginUrl || '/account/login'));
    login.textContent = 'Sign in';
    return login;
  };

  const ensureColdGuestSidebar = (maker) => {
    if (coldGuestSidebar && document.contains(coldGuestSidebar)) return coldGuestSidebar;
    const sidebar = document.createElement('aside');
    sidebar.className = 'threadly-ai-sidebar';
    sidebar.setAttribute('data-threadly-sidebar', '');
    sidebar.setAttribute('data-threadly-cold-guest-sidebar', 'true');
    sidebar.setAttribute('aria-label', 'Maker sidebar');
    sidebar.setAttribute('aria-busy', 'true');
    sidebar.setAttribute('data-threadly-cold-state', 'loading');

    const top = document.createElement('div');
    top.className = 'threadly-ai-sidebar__top';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'threadly-ai-icon-button';
    close.setAttribute('data-threadly-sidebar-toggle', '');
    close.setAttribute('aria-label', 'Close sidebar');
    close.addEventListener('click', () => {
      pendingSidebarToggle = false;
      maker.classList.remove('is-levyo-cold-sidebar-open');
    });
    top.append(close);

    const searchLabel = document.createElement('label');
    searchLabel.className = 'threadly-ai-search';
    const search = document.createElement('input');
    search.type = 'search';
    search.name = 'threadly_ai_chat_search';
    search.placeholder = 'Search';
    search.setAttribute('aria-label', 'Search chats');
    search.setAttribute('aria-disabled', 'true');
    search.disabled = true;
    searchLabel.append(search);

    const newChat = document.createElement('button');
    newChat.type = 'button';
    newChat.className = 'threadly-ai-sidebar-action is-active';
    newChat.setAttribute('data-threadly-new-chat', '');
    newChat.setAttribute('data-threadly-cold-action', '');
    newChat.setAttribute('aria-disabled', 'true');
    newChat.disabled = true;
    const newChatText = document.createElement('span');
    newChatText.textContent = 'New Chat';
    newChat.append(newChatText);

    const sectionTitle = document.createElement('div');
    sectionTitle.className = 'threadly-ai-sidebar__section-title';
    sectionTitle.textContent = 'Chats';
    const chatList = document.createElement('div');
    chatList.className = 'threadly-ai-chat-list';
    chatList.setAttribute('data-threadly-chat-list', '');
    const status = document.createElement('p');
    status.className = 'threadly-ai-sidebar__section-title';
    status.setAttribute('data-threadly-cold-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = 'Loading Maker…';
    const footer = document.createElement('div');
    footer.className = 'threadly-ai-sidebar__footer threadly-ai-sidebar__footer--guest';
    footer.setAttribute('data-threadly-profile-footer', '');
    footer.append(createColdGuestSignInControl());

    sidebar.append(
      top,
      searchLabel,
      newChat,
      createColdSidebarAction('My current shirt', 'data-threadly-current-shirt'),
      createColdSidebarAction('My designs', 'data-threadly-my-designs'),
      sectionTitle,
      chatList,
      status,
      footer
    );
    maker.append(sidebar);
    coldGuestSidebar = sidebar;
    return sidebar;
  };

  const disposeColdGuestSidebar = () => {
    if (coldRuntimeErrorListener) {
      document.removeEventListener('threadly:maker-runtime-error', coldRuntimeErrorListener);
      coldRuntimeErrorListener = null;
    }
    coldGuestSidebar?.remove?.();
    coldGuestSidebar = null;
  };

  const setColdGuestSidebarState = (state, message = '') => {
    const sidebar = coldGuestSidebar;
    if (!sidebar) return;
    sidebar.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    sidebar.setAttribute('data-threadly-cold-state', state);
    const status = sidebar.querySelector('[data-threadly-cold-status]');
    if (!status) return;
    status.textContent = message || (state === 'error' ? 'Maker could not load.' : 'Loading Maker…');
    status.setAttribute('role', state === 'error' ? 'alert' : 'status');
    status.setAttribute('aria-live', state === 'error' ? 'assertive' : 'polite');
  };

  const showColdGuestRuntimeError = (message = 'Maker could not load. Try again.') => {
    if (!coldGuestSidebar) return;
    setColdGuestSidebarState('error', message);
    if (coldGuestSidebar.querySelector('[data-threadly-cold-retry]')) return;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'threadly-ai-sidebar-action threadly-ai-sidebar-action--plain';
    retry.setAttribute('data-threadly-cold-retry', '');
    retry.textContent = 'Try again';
    retry.addEventListener('click', () => {
      retry.remove();
      coldRuntimeRequested = false;
      setColdGuestSidebarState('loading');
      requestMakerRuntime();
    });
    coldGuestSidebar.append(retry);
  };

  const bindColdRuntimeErrorListener = () => {
    if (coldRuntimeErrorListener) return;
    coldRuntimeErrorListener = () => showColdGuestRuntimeError();
    document.addEventListener('threadly:maker-runtime-error', coldRuntimeErrorListener);
  };

  const openColdGuestSidebar = (maker) => {
    ensureColdGuestSidebar(maker);
    enhanceSidebar(maker);
    syncSidebarViewportState(maker);
    setColdGuestSidebarState('loading');
    bindColdRuntimeErrorListener();
    pendingSidebarToggle = true;
    document.dispatchEvent(new CustomEvent('threadly:levyo-sidebar-open-intent', { detail: { maker } }));
    maker.classList.add('is-levyo-cold-sidebar-open');
    requestMakerRuntime();
    return true;
  };

  const closeColdGuestSidebar = (maker) => {
    pendingSidebarToggle = false;
    maker.classList.remove('is-levyo-cold-sidebar-open');
    return true;
  };

  const requestSidebarAction = (action = 'toggle', maker = currentMaker()) => {
    if (!maker) return false;
    const coldOpen = maker.classList.contains('is-levyo-cold-sidebar-open');
    if (coldOpen && (action === 'close' || action === 'toggle')) return closeColdGuestSidebar(maker);
    if (!window.THREADLY_AI_MAKER_RUNTIME_LOADED) return action === 'toggle' ? openColdGuestSidebar(maker) : false;
    const toggle = maker.querySelector('[data-threadly-sidebar-toggle]');
    const visiblyOpen = maker.classList.contains('is-sidebar-open') || maker.classList.contains('is-sidebar-opening');
    if (action === 'close' || (action === 'toggle' && visiblyOpen)) {
      if (!toggle) return false;
      toggle.click();
      return true;
    }
    const host = maker.closest('#threadly-konva-designer');
    const makerView = host?.classList.contains('threadly-cart-open')
      ? 'cart'
      : host?.classList.contains('threadly-select-open')
        ? 'current-shirt'
        : 'create';
    document.dispatchEvent(new CustomEvent('threadly:levyo-sidebar-open-intent', { detail: { maker } }));
    document.dispatchEvent(new CustomEvent('threadly:levyo-sidebar-open-requested', {
      detail: {
        source: 'fullscreen-header',
        preserveMakerView: makerView !== 'create',
        makerView,
      }
    }));
    return true;
  };

  const exploreLinks = [
    {
      href: '/pages/ai-tshirt-maker-faq',
      title: 'Help and FAQs',
      icon: icons.question
    },
    {
      href: '/ai-tshirt-maker/how-it-works',
      title: 'How Levyo works',
      icon: icons.info
    },
    {
      href: '/pages/choose-your-occasion',
      title: 'Choose your occasion',
      icon: icons.box
    },
    {
      href: '/pages/shape-my-idea',
      title: 'Shape my idea',
      icon: icons.question
    },
    {
      href: config.accountUrl || '/account',
      title: 'Orders',
      icon: icons.box
    },
    {
      href: '/pages/uk-delivery-returns-custom-shirts',
      title: 'Delivery and returns',
      icon: icons.info
    },
    {
      href: '/pages/customer-proof-quality-promise',
      title: 'Quality promise',
      icon: icons.info
    }
  ];

  const openRequestedView = (designer) => {
    if (!designer || designer.dataset.levyoInitialViewHandled === 'true' || initialViewAttemptStarted) return;
    if (initialRequestedView !== 'cart') return;
    initialViewAttemptStarted = true;
    let url;
    try {
      url = new URL(window.location.href);
    } catch (_error) {
      return;
    }
    let attempts = 0;
    const finish = () => {
      designer.dataset.levyoInitialViewHandled = 'true';
      url.searchParams.delete('view');
      window.history.replaceState({}, document.title, url.toString());
    };
    const tryOpen = () => {
      if (designer.classList.contains('threadly-cart-open')) {
        finish();
        return;
      }
      designer.querySelector('[data-threadly-cart-open]')?.click();
      if (designer.classList.contains('threadly-cart-open')) {
        finish();
        return;
      }
      attempts += 1;
      if (attempts < initialViewMaxAttempts) window.setTimeout(tryOpen, 100);
    };
    window.setTimeout(tryOpen, 80);
  };

  const makeExplorePanel = (onBack) => {
    const panel = document.createElement('nav');
    panel.className = 'levyo-maker-explore';
    panel.setAttribute('aria-label', 'Explore Levyo');
    panel.setAttribute('aria-hidden', 'true');

    const header = document.createElement('div');
    header.className = 'levyo-maker-explore__header';

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'levyo-maker-explore__back';
    back.setAttribute('aria-label', 'Back to Create');
    if (icons.arrow) {
      const backIcon = document.createElement('img');
      backIcon.src = icons.arrow;
      backIcon.alt = '';
      back.append(backIcon);
    } else {
      back.textContent = 'Back';
    }
    back.addEventListener('click', onBack);

    const title = document.createElement('h2');
    title.className = 'levyo-maker-explore__title';
    title.textContent = 'Explore Levyo';
    header.append(back, title);

    const links = document.createElement('div');
    links.className = 'levyo-maker-explore__links';

    exploreLinks.forEach(({ href, title: linkTitle, icon }) => {
      const link = document.createElement('a');
      link.className = 'levyo-maker-explore__link';
      link.href = href;

      if (icon) {
        const image = document.createElement('img');
        image.src = icon;
        image.alt = '';
        link.append(image);
      }

      const label = document.createElement('span');
      label.textContent = linkTitle;
      link.append(label);
      links.append(link);
    });

    panel.append(header, links);
    return panel;
  };

  const setExploreMode = (sidebar, trigger, explorePanel, mode) => {
    const showExplore = mode === 'explore';
    if (showExplore) {
      const back = explorePanel.querySelector('.levyo-maker-explore__back');
      explorePanel.classList.add('is-levyo-focus-transfer-target');
      explorePanel.setAttribute('aria-hidden', 'false');
      back?.focus?.({ preventScroll: true });
      sidebar.classList.add('is-levyo-explore');
      explorePanel.classList.remove('is-levyo-focus-transfer-target');
      trigger.setAttribute('aria-expanded', 'true');
      return;
    }

    trigger.classList.add('is-levyo-focus-transfer-target');
    trigger.focus?.({ preventScroll: true });
    sidebar.classList.remove('is-levyo-explore');
    trigger.classList.remove('is-levyo-focus-transfer-target');
    trigger.setAttribute('aria-expanded', 'false');
    explorePanel.setAttribute('aria-hidden', 'true');
  };

  const enhanceSidebar = (maker) => {
    if (document.querySelector('body > .threadly-ai-sidebar.levyo-maker-sidebar-portal')) return true;
    const sidebar = maker.querySelector('.threadly-ai-sidebar');
    if (!sidebar || sidebar.dataset.levyoUnifiedSidebar === 'true') return false;

    sidebar.dataset.levyoUnifiedSidebar = 'true';

    let trigger;
    let explorePanel;
    const setMode = (mode) => setExploreMode(sidebar, trigger, explorePanel, mode);
    explorePanel = makeExplorePanel(() => setMode('create'));

    trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'threadly-ai-sidebar-action levyo-maker-explore-trigger';
    trigger.setAttribute('data-threadly-protected-sidebar-action', '');
    trigger.setAttribute('aria-controls', 'levyo-maker-explore-panel');
    trigger.setAttribute('aria-expanded', 'false');
    const triggerLabel = document.createElement('span');
    triggerLabel.textContent = 'Explore Levyo';
    trigger.append(triggerLabel);
    if (icons.arrow) {
      const triggerIcon = document.createElement('img');
      triggerIcon.src = icons.arrow;
      triggerIcon.alt = '';
      trigger.append(triggerIcon);
    }
    trigger.addEventListener('click', () => {
      if (!maker.classList.contains('is-authoritative-account')) {
        const login = sidebar.querySelector('[data-threadly-profile-footer] a[href]');
        if (login) {
          login.click();
        } else {
          window.location.assign(String(window.THREADLY_AI_MAKER_CONFIG?.loginUrl || '/account/login'));
        }
        return;
      }
      setMode('explore');
    });

    explorePanel.id = 'levyo-maker-explore-panel';
    const sectionTitle = sidebar.querySelector('.threadly-ai-sidebar__section-title');
    const guestAccountSheetEnabled = Boolean(
      String(window.THREADLY_AI_MAKER_CONFIG?.shopifyAccountSheetMarkup || '').trim()
    );
    const guestAccountGate = guestAccountSheetEnabled
      ? document.createElement('shopify-account')
      : null;
    if (guestAccountGate) {
      guestAccountGate.className = 'threadly-ai-sidebar-account-gate levyo-maker-explore-account-gate';
      guestAccountGate.setAttribute('data-threadly-sidebar-account-gate', 'sidebar-explore');
      guestAccountGate.setAttribute('data-threadly-shopify-account', 'maker-sidebar-explore');
      guestAccountGate.setAttribute('menu', 'customer-account-main-menu');
      guestAccountGate.setAttribute('sign-in-url', String(window.THREADLY_AI_MAKER_CONFIG?.loginUrl || ''));
      const guestExploreLabel = document.createElement('span');
      guestExploreLabel.className = 'threadly-ai-sidebar-action levyo-maker-explore-trigger';
      guestExploreLabel.setAttribute('slot', 'signed-out-avatar');
      const guestExploreText = document.createElement('span');
      guestExploreText.textContent = 'Explore Levyo';
      guestExploreLabel.append(guestExploreText);
      if (icons.arrow) {
        const guestExploreIcon = document.createElement('img');
        guestExploreIcon.src = icons.arrow;
        guestExploreIcon.alt = '';
        guestExploreLabel.append(guestExploreIcon);
      }
      guestAccountGate.append(guestExploreLabel);
    }
    const guestSafe = maker.querySelector('.threadly-ai-composer')?.classList.contains('is-logged-out') === true;
    trigger.hidden = guestSafe && Boolean(guestAccountGate);
    if (guestAccountGate) guestAccountGate.hidden = !guestSafe;
    sidebar.insertBefore(trigger, sectionTitle || sidebar.querySelector('.threadly-ai-sidebar__footer'));
    if (guestAccountGate) {
      sidebar.insertBefore(guestAccountGate, sectionTitle || sidebar.querySelector('.threadly-ai-sidebar__footer'));
    }
    sidebar.insertBefore(explorePanel, sidebar.querySelector('.threadly-ai-sidebar__footer'));

    sidebar.classList.add('levyo-maker-sidebar-portal');
    sidebar.classList.toggle('is-levyo-portal-open', maker.classList.contains('is-sidebar-open'));
    document.body.append(sidebar);
    sidebar.querySelectorAll('shopify-account').forEach((movedAccount) => {
      const liveAccount = document.createElement('shopify-account');
      Array.from(movedAccount.attributes || []).forEach((attribute) => {
        liveAccount.setAttribute(attribute.name, attribute.value);
      });
      liveAccount.innerHTML = movedAccount.innerHTML;
      movedAccount.replaceWith(liveAccount);
    });
    return true;
  };

  const stableSidebarTargetState = new Map();
  const stableAccountVisualTargets = new WeakMap();
  const stableGuestAccountSelector =
    'body > shopify-account[data-threadly-stable-guest-account]';
  const guestAccountOverlayEnabled = () => (
    window.THREADLY_AI_MAKER_CONFIG?.guestShopifyAccountOverlayEnabled === true
  );
  const shopifyAccountComponentReady = () => (
    typeof window.customElements?.get === 'function' &&
    Boolean(window.customElements.get('shopify-account'))
  );
  const clickNativeShopifyAccountButton = (account) => {
    const button = account?.shadowRoot?.querySelector?.(
      'button[part="signed-out-avatar"], button.account-button, button'
    );
    if (!button || typeof button.click !== 'function') return false;
    button.click();
    return true;
  };
  const shopifyAccountLoginUrl = (account) => {
    const configured = String(account?.getAttribute?.('sign-in-url') || '').trim();
    if (configured) return configured;
    const bypass = document.querySelector?.('a[data-threadly-login-bypass][href]');
    return String(bypass?.href || bypass?.getAttribute?.('href') || '/account/login').trim();
  };
  const redirectToShopifyAccountLogin = (account) => {
    const href = shopifyAccountLoginUrl(account);
    if (!href) return false;
    try {
      // The Shopify storefront runtime may expose a location wrapper without
      // assign()/replace().  The href setter is the browser-native fallback
      // that remains available in that realm and still preserves return_to.
      window.location.href = href;
      return true;
    } catch (_error) {
      return false;
    }
  };
  const scheduleShopifyAccountLoginFallback = (account) => {
    if (!account || account.dataset.threadlyLoginFallbackPending === 'true') return;
    account.dataset.threadlyLoginFallbackPending = 'true';
    window.setTimeout?.(() => {
      account.removeAttribute('data-threadly-login-fallback-pending');
      if (!account.isConnected || account.shadowRoot?.querySelector?.('dialog')?.open === true) return;
      redirectToShopifyAccountLogin(account);
    }, 180);
  };
  const openNativeShopifyAccountOrRedirect = (account) => {
    const dialog = () => account?.shadowRoot?.querySelector?.('dialog');
    if (dialog()?.open === true) return true;
    clickNativeShopifyAccountButton(account);
    if (dialog()?.open === true) return true;
    // Shopify's web component can finish hydrating one task after the click.
    // Give that native sheet a short chance; never leave a customer with a
    // dead-looking control when the component still has not opened it.
    scheduleShopifyAccountLoginFallback(account);
    return false;
  };
  window.threadlyLevyoOpenGuestAccount = (key) => {
    if (!guestAccountOverlayEnabled() || !shopifyAccountComponentReady()) return false;
    const account = document.querySelector(
      `body > shopify-account[data-threadly-stable-sidebar-account="${key}"][data-levyo-sidebar-account-active="true"]`
    );
    if (!account || account.hidden || account.inert || account.getAttribute('aria-hidden') === 'true') return false;
    const trigger = account.querySelector('[slot="signed-out-avatar"]');
    if (!trigger || typeof trigger.click !== 'function') return false;
    trigger.click();
    // The stable host is an invisible proxy around Shopify's real Account
    // control.  Only report success when Shopify actually opened its native
    // dialog; otherwise the caller must use the official page-login fallback.
    return openNativeShopifyAccountOrRedirect(account);
  };
  // This supported slot is only a Shopify-owned session hint. It never grants
  // Maker Owner authority without an exact protected customer read.
  function shopifyAccountSessionHint() {
    if (!shopifyAccountComponentReady()) return 0;
    const accounts = [
      document.querySelector('shopify-account[data-threadly-shopify-account="auth-probe"]'),
      document.querySelector(
        'body > shopify-account[data-threadly-stable-guest-account][data-levyo-sidebar-account-active="true"]'
      ),
      document.querySelector('shopify-account[data-threadly-shopify-account="header"]'),
    ];
    for (const account of accounts) {
      const signedOutAvatar = account?.querySelector?.('[slot="signed-out-avatar"]');
      const authProbe = account?.getAttribute?.('data-threadly-shopify-account') === 'auth-probe';
      if (!account || !signedOutAvatar || account.hidden ||
        (!authProbe && account.getAttribute('aria-hidden') === 'true')) continue;
      const accountStyle = window.getComputedStyle?.(account);
      const accountRect = account.getBoundingClientRect?.();
      if (accountStyle?.display === 'none' || accountStyle?.visibility === 'hidden' ||
        !accountRect || accountRect.width < 1 || accountRect.height < 1) continue;
      const signedOutStyle = window.getComputedStyle?.(signedOutAvatar);
      const signedOutRects = signedOutAvatar.getClientRects?.();
      return signedOutAvatar.hidden ||
        signedOutAvatar.getAttribute('aria-hidden') === 'true' ||
        signedOutStyle?.display === 'none' ||
        signedOutStyle?.visibility === 'hidden' ||
        Boolean(signedOutRects && signedOutRects.length === 0) ? 1 : -1;
    }
    return 0;
  }
  function shopifyAccountSignedInRecoveryHint() {
    return shopifyAccountSessionHint() === 1;
  }
  window.__lsh = shopifyAccountSessionHint;
  window.__lsw = async () => {
    const deadline = Date.now() + 1200;
    let hint = 0;
    do {
      hint = shopifyAccountSessionHint();
      if (hint > 0) return true;
      if (Date.now() >= deadline) {
        if (hint < 0) await window.THREADLY?.logout?.();
        return false;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    } while (true);
  };
  const stableSidebarAccountSpecs = [
    {
      key: 'sidebar-new-chat',
      selectors: [
        '[data-threadly-new-chat][data-threadly-protected-sidebar-action]',
        '[data-threadly-sidebar-account-gate="sidebar-new-chat"]',
        '[data-threadly-cold-guest-sidebar] [data-threadly-new-chat]',
      ],
    },
    {
      key: 'sidebar-my-designs',
      selectors: [
        '[data-threadly-my-designs][data-threadly-protected-sidebar-action]',
        '[data-threadly-sidebar-account-gate="sidebar-my-designs"]',
        '[data-threadly-cold-guest-sidebar] [data-threadly-my-designs]',
        '.levyo-maker-explore-trigger[data-threadly-protected-sidebar-action]',
        '[data-threadly-sidebar-account-gate="sidebar-explore"]',
      ],
    },
  ];
  const stableComposerAccountSpec = {
    key: 'composer-login',
    selectors: ['[data-threadly-composer-login]'],
  };

  const restoreStableSidebarTargets = () => {
    stableSidebarTargetState.forEach((state, target) => {
      if (!target?.isConnected) return;
      target.style.pointerEvents = state.pointerEvents;
      if (state.ariaHidden === null) target.removeAttribute('aria-hidden');
      else target.setAttribute('aria-hidden', state.ariaHidden);
      target.inert = state.inert;
      target.classList.remove('is-levyo-account-proxy-focus');
    });
    stableSidebarTargetState.clear();
    document.querySelectorAll(stableGuestAccountSelector).forEach((account) => {
      stableAccountVisualTargets.delete(account);
    });
  };

  const deactivateStableSidebarAccount = (account) => {
    if (!account) return;
    account.hidden = true;
    account.inert = true;
    account.setAttribute('aria-hidden', 'true');
    account.removeAttribute('data-levyo-sidebar-account-active');
  };

  const stableGuestAccountKeys = [
    ...stableSidebarAccountSpecs.map(({ key }) => key),
    stableComposerAccountSpec.key,
  ];

  const ensureStableGuestAccountHosts = () => {
    if (!guestAccountOverlayEnabled() || !shopifyAccountComponentReady()) return new Map();
    const existing = new Map(
      Array.from(document.querySelectorAll(stableGuestAccountSelector))
        .map((account) => [account.getAttribute('data-threadly-stable-guest-account'), account])
    );
    stableGuestAccountKeys.forEach((key) => {
      if (existing.has(key)) return;
      const account = document.createElement('shopify-account');
      account.className = 'levyo-maker-stable-sidebar-account levyo-maker-stable-guest-account';
      account.setAttribute('data-threadly-shopify-account', `maker-${key}`);
      account.setAttribute('data-threadly-stable-sidebar-account', key);
      account.setAttribute('data-threadly-stable-guest-account', key);
      account.setAttribute('menu', 'customer-account-main-menu');
      account.setAttribute('sign-in-url', String(window.THREADLY_AI_MAKER_CONFIG?.loginUrl || ''));
      const label = document.createElement('a');
      label.setAttribute('slot', 'signed-out-avatar');
      label.setAttribute('data-threadly-login-bypass', 'true');
      label.href = String(window.THREADLY_AI_MAKER_CONFIG?.loginUrl || '/account/login');
      label.textContent = key === stableComposerAccountSpec.key ? 'Log in' : 'Sign in';
      label.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openNativeShopifyAccountOrRedirect(account);
      });
      account.addEventListener('click', () => {
        // Shopify's visible shadow button can consume the click before the
        // slotted fallback anchor sees it. Observe the host as the final
        // safety net, without intercepting Shopify's native event.
        scheduleShopifyAccountLoginFallback(account);
      }, true);
      account.append(label);
      account.addEventListener('focusin', () => {
        stableAccountVisualTargets.get(account)?.forEach((target) => {
          target.classList.add('is-levyo-account-proxy-focus');
        });
      });
      account.addEventListener('focusout', () => {
        stableAccountVisualTargets.get(account)?.forEach((target) => {
          target.classList.remove('is-levyo-account-proxy-focus');
        });
      });
      deactivateStableSidebarAccount(account);
      document.body.append(account);
      existing.set(key, account);
    });
    return existing;
  };

  const removeStableGuestAccountHosts = () => {
    document.querySelectorAll(stableGuestAccountSelector).forEach((account) => {
      if (account.classList.contains('is-levyo-account-sheet-open')) return;
      deactivateStableSidebarAccount(account);
      account.remove();
    });
  };

  const prepareStableGuestSidebarVisuals = (sidebar, label = 'Sign in') => {
    if (!sidebar) return;
    sidebar.querySelectorAll('[data-threadly-protected-sidebar-action]').forEach((action) => {
      action.hidden = false;
      action.inert = false;
      action.removeAttribute('aria-hidden');
    });
    sidebar.querySelectorAll('[data-threadly-sidebar-account-gate]').forEach((account) => {
      account.hidden = true;
      account.inert = true;
      account.setAttribute('aria-hidden', 'true');
    });
    const footer = sidebar.querySelector('[data-threadly-profile-footer]');
    if (!footer) return;
    footer.classList.add('threadly-ai-sidebar__footer--stable-account');
    footer.querySelectorAll('a.threadly-ai-profile-login[data-threadly-login-bypass]').forEach((link) => {
      link.hidden = true;
      link.inert = true;
      link.setAttribute('aria-hidden', 'true');
    });
    let visual = footer.querySelector('[data-threadly-profile-login-visual]');
    if (!visual) {
      visual = document.createElement('span');
      visual.className = 'threadly-ai-profile-login';
      visual.setAttribute('data-threadly-profile-login-visual', '');
      footer.prepend(visual);
    }
    visual.textContent = label;
    visual.hidden = false;
    visual.inert = false;
    visual.removeAttribute('aria-hidden');
    footer.querySelectorAll('shopify-account').forEach((account) => {
      account.hidden = true;
      account.inert = true;
      account.setAttribute('aria-hidden', 'true');
    });
  };

  const findStableSidebarTargets = (sidebar, selectors) => {
    if (!sidebar) return null;
    const targets = [];
    selectors.forEach((selector) => {
      const target = sidebar.querySelector(selector);
      if (!target || target.hidden || targets.includes(target)) return;
      const rect = target.getBoundingClientRect();
      if (rect.width >= 1 && rect.height >= 1) targets.push(target);
    });
    if (!targets.length) return null;
    const rects = targets.map((target) => target.getBoundingClientRect());
    const left = Math.min(...rects.map((rect) => rect.left));
    const top = Math.min(...rects.map((rect) => rect.top));
    const right = Math.max(...rects.map((rect) => rect.right));
    const bottom = Math.max(...rects.map((rect) => rect.bottom));
    return {
      targets,
      rect: { left, top, width: right - left, height: bottom - top },
    };
  };

  const syncStableSidebarAccountGates = (sidebar, active) => {
    const activeMaker = currentMaker();
    const pendingShopify = activeMaker?.classList.contains('is-pending-shopify-account');
    const guestSafe = Boolean(
      guestAccountOverlayEnabled() &&
      shopifyAccountComponentReady() &&
      activeMaker &&
      !activeMaker.classList.contains('is-authoritative-account') &&
      !pendingShopify &&
      window.THREADLY?.SHOPIFY_LOGGED_IN !== true &&
      activeMaker.querySelector('.threadly-ai-composer.is-logged-out')
    );
    const accounts = new Map(
      Array.from(document.querySelectorAll(stableGuestAccountSelector))
        .map((account) => [account.getAttribute('data-threadly-stable-guest-account'), account])
    );
    // Shopify owns the open sheet and its form. Never toggle hidden/inert or
    // rewrite the host geometry while that form is active: doing so can leave
    // Shopify's enabled submit control without a rendered hit target.
    if (Array.from(accounts.values()).some((account) => (
      account.classList.contains('is-levyo-account-sheet-open')
    ))) return;
    restoreStableSidebarTargets();
    accounts.forEach(deactivateStableSidebarAccount);
    if (!guestSafe) {
      const footer = sidebar?.querySelector('[data-threadly-profile-footer]');
      footer?.classList.remove('threadly-ai-sidebar__footer--stable-account');
      const allowLegacyGuestFallback = Boolean(
        activeMaker &&
        activeMaker.classList.contains('is-logged-out') &&
        !pendingShopify &&
        window.THREADLY?.SHOPIFY_LOGGED_IN !== true &&
        (!guestAccountOverlayEnabled() || !shopifyAccountComponentReady())
      );
      footer?.querySelectorAll('a.threadly-ai-profile-login[data-threadly-login-bypass]').forEach((link) => {
        link.hidden = !allowLegacyGuestFallback;
        link.inert = !allowLegacyGuestFallback;
        if (allowLegacyGuestFallback) link.removeAttribute('aria-hidden');
        else link.setAttribute('aria-hidden', 'true');
      });
      const visual = sidebar?.querySelector('[data-threadly-profile-login-visual]');
      if (visual) visual.hidden = true;
      removeStableGuestAccountHosts();
      return;
    }
    const liveAccounts = ensureStableGuestAccountHosts();
    const activateAccount = (rootNode, { key, selectors }) => {
      if (!rootNode) return;
      const account = liveAccounts.get(key);
      const match = findStableSidebarTargets(rootNode, selectors);
      if (!account || !match) return;
      const { targets, rect } = match;
      stableAccountVisualTargets.set(account, targets);
      targets.forEach((target) => {
        stableSidebarTargetState.set(target, {
          pointerEvents: target.style.pointerEvents,
          ariaHidden: target.getAttribute('aria-hidden'),
          inert: target.inert,
        });
        target.style.pointerEvents = 'none';
        target.setAttribute('aria-hidden', 'true');
        target.inert = true;
      });

      account.style.setProperty('left', `${rect.left}px`);
      account.style.setProperty('top', `${rect.top}px`);
      account.style.setProperty('width', `${rect.width}px`);
      account.style.setProperty('height', `${rect.height}px`);
      account.hidden = false;
      account.inert = false;
      account.setAttribute('aria-hidden', 'false');
      account.setAttribute('data-levyo-sidebar-account-active', 'true');
    };

    // Keep the visible composer CTA interactive.  It already has the
    // first-party Shopify page-login handler in the Maker runtime; overlaying
    // it with a lazy Shopify proxy can make the control inert when the native
    // account sheet has not hydrated.  Stable proxies remain enabled for the
    // protected sidebar actions below.
    if (!active || !sidebar) return;
    prepareStableGuestSidebarVisuals(sidebar, 'Sign in');
    stableSidebarAccountSpecs.forEach((spec) => {
      activateAccount(sidebar, spec);
    });
  };

  if (
    guestAccountOverlayEnabled() &&
    typeof window.customElements?.whenDefined === 'function' &&
    !shopifyAccountComponentReady()
  ) {
    window.customElements.whenDefined('shopify-account').then(() => {
      const maker = currentMaker();
      if (!maker) return;
      if (activeSidebarBinding?.maker === maker) activeSidebarBinding.sync();
      else syncSidebarViewportState(maker);
    }).catch(() => {});
  }

  let stableGuestAccountRecoveryTimers = [];
  const clearStableGuestAccountRecovery = () => {
    stableGuestAccountRecoveryTimers.forEach(window.clearTimeout);
    stableGuestAccountRecoveryTimers = [];
  };
  const recoverStableGuestAccount = () => {
    clearStableGuestAccountRecovery();
    // Start the already ticket-validated recovery immediately. Shopify Account
    // may paint its signed-in slot before the first delayed poll; waiting for
    // the old 1.2s/3.5s cadence can otherwise defer a verified callback past
    // the Maker synchronization blocker.
    [0, 300, 1200, 3500, 8000, 18000, 40000].forEach((delay) => {
      stableGuestAccountRecoveryTimers.push(window.setTimeout(async () => {
        if (currentMaker()?.classList.contains('is-authoritative-account')) {
          clearStableGuestAccountRecovery();
          return;
        }
        let probeOnlyAttemptAt = 0;
        try {
          probeOnlyAttemptAt = Number(sessionStorage.getItem('threadly_sap_v1') || 0);
        } catch (_) {}
        if (probeOnlyAttemptAt && shopifyAccountSignedInRecoveryHint()) {
          clearStableGuestAccountRecovery();
          const state = await window.threadlyLevyoShopifyRecoverFromSignedInHint?.(true).catch(() => 0);
          if (state === 1) {
            if (activeSidebarBinding?.maker === currentMaker()) activeSidebarBinding.sync();
            else syncSidebarViewportState(currentMaker());
          }
          return;
        }
        const terminal = delay === 40000;
        if (probeOnlyAttemptAt) {
          if (terminal) {
            ['threadly_sap_v1', 'threadly_li', 'threadly_lc'].forEach((key) => {
              try { sessionStorage.removeItem(key); } catch (_) {}
            });
          }
          return;
        }
        if (!terminal) {
          try {
            const attemptAt = sessionStorage.getItem('threadly_sap_v1');
            if (attemptAt) sessionStorage.setItem('threadly_li', attemptAt);
          } catch (_) {}
        }
        const state = await window.threadlyLevyoShopifyProbe?.().catch(() => 0);
        if (terminal) {
          ['threadly_sap_v1', 'threadly_li', 'threadly_lc'].forEach((key) => {
            try { sessionStorage.removeItem(key); } catch (_) {}
          });
        }
        if (state !== 1) return;
        clearStableGuestAccountRecovery();
        if (activeSidebarBinding?.maker === currentMaker()) activeSidebarBinding.sync();
        else syncSidebarViewportState(currentMaker());
      }, delay));
    });
  };
  window.addEventListener('threadly:l', recoverStableGuestAccount, { passive: true });
  try {
    if (Date.now() - Number(sessionStorage.getItem('threadly_li') || 0) < 300000) {
      recoverStableGuestAccount();
    }
  } catch (_) {}

  const connectHeaderMenu = () => {
    const headerMenu = document.querySelector('.section-header .header__icon--menu');
    headerMenu?.setAttribute('aria-label', 'Open Levyo menu');
    if (root.dataset.levyoMakerMenuBridge === 'true') return;

    root.dataset.levyoMakerMenuBridge = 'true';
    document.addEventListener('click', (event) => {
      const activeHeaderMenu = event.target?.closest?.('.section-header .header__icon--menu');
      if (!activeHeaderMenu) return;
      const maker = currentMaker();
      if (!maker) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      activeHeaderMenu.setAttribute('aria-label', 'Open Levyo menu');
      // A section/runtime refresh can replace the Maker node without
      // reloading this asset. Rebind the viewport mirror at the exact user
      // intent boundary before dispatching to the current runtime instance.
      syncSidebarViewportState(maker);

      const drawer = document.querySelector('#Details-menu-drawer-container');
      drawer?.removeAttribute('open');
      drawer?.classList.remove('menu-opening');
      if (!requestSidebarAction('toggle', maker)) pendingSidebarToggle = !pendingSidebarToggle;
    }, true);
  };

  const arrangeMakerHeader = (maker) => {
    const siteHeader = document.querySelector('.section-header .header');
    const leftCluster = siteHeader?.querySelector('.mobile-left-cluster');
    const headerIcons = siteHeader?.querySelector('.header__icons');
    const heading = siteHeader?.querySelector('.header__heading') || siteHeader?.querySelector('.header__heading-link');
    const account = siteHeader?.querySelector('.header__account-wrapper');
    const newChat = siteHeader?.querySelector('.threadly-ai-topbar__new') ||
      maker.querySelector('.threadly-ai-topbar__new[data-threadly-new-chat]');
    const cart = siteHeader?.querySelector('.threadly-ai-topbar__cart') ||
      maker.querySelector('.threadly-ai-topbar__cart[data-threadly-maker-cart]');
    if (account) {
      account.hidden = true;
      account.inert = true;
      account.setAttribute('aria-hidden', 'true');
    }
    if (!siteHeader || !leftCluster || !headerIcons || !heading || !newChat || !cart) return false;

    let center = siteHeader.querySelector('.levyo-maker-header-center');
    if (!center) {
      center = document.createElement('div');
      center.className = 'levyo-maker-header-center';
      siteHeader.insertBefore(center, headerIcons);
    }
    if (heading.parentElement !== center) center.append(heading);

    let actions = siteHeader.querySelector('.levyo-maker-header-actions');
    if (!actions) {
      actions = document.createElement('div');
      actions.className = 'levyo-maker-header-actions';
      siteHeader.insertBefore(actions, headerIcons);
    }
    if (newChat.parentElement !== actions) actions.append(newChat);
    if (cart.parentElement !== actions) actions.append(cart);

    root.classList.add('levyo-maker-header-integrated');
    window.requestAnimationFrame(syncHeaderHeight);
    return true;
  };

  const ensureSidebarBackdrop = () => {
    let backdrop = document.querySelector('body > .levyo-maker-sidebar-backdrop');
    if (backdrop) return backdrop;

    backdrop = document.createElement('button');
    backdrop.type = 'button';
    backdrop.className = 'levyo-maker-sidebar-backdrop';
    backdrop.setAttribute('aria-label', 'Close sidebar');
    backdrop.addEventListener('click', () => {
      const maker = currentMaker();
      if (!maker?.classList.contains('is-sidebar-open') && !maker?.classList.contains('is-levyo-cold-sidebar-open')) return;
      requestSidebarAction('close', maker);
      window.setTimeout(() => {
        document.querySelector('.section-header .header__icon--menu')?.focus?.({ preventScroll: true });
      }, 330);
    });
    document.body.append(backdrop);
    return backdrop;
  };

  const sidebarFocusableElements = (sidebar) => {
    if (!sidebar) return [];
    const sidebarElements = Array.from(sidebar.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    const stableAccounts = Array.from(document.querySelectorAll?.(
      'body > shopify-account[data-threadly-stable-sidebar-account][data-levyo-sidebar-account-active="true"]'
    ) || []);
    return sidebarElements.concat(stableAccounts).filter((node) => {
      if (node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
      if (typeof node.getClientRects === 'function' && !node.getClientRects().length) return false;
      const style = typeof window.getComputedStyle === 'function' ? window.getComputedStyle(node) : null;
      return style?.display !== 'none' && style?.visibility !== 'hidden';
    }).sort((a, b) => (
      Number(a.getBoundingClientRect?.().top || 0) -
      Number(b.getBoundingClientRect?.().top || 0)
    ));
  };

  const syncSidebarViewportState = (maker) => {
    if (activeSidebarBinding?.maker === maker) {
      activeSidebarBinding.sync();
      return activeSidebarBinding;
    }
    activeSidebarBinding?.dispose();

    let closeTimer = 0;
    let interactionOpen = false;
    let historyArmed = false;
    let returnFocus = null;
    let restorePending = false;
    let sidebar = null;
    let observedSidebar = null;
    let sidebarObserver = null;
    let accountGeometryFrame = 0;
    let accountGeometryFramesRemaining = 0;
    let stableAccountsVisible = false;
    let disposed = false;
    const resolveSidebar = () => {
      sidebar = document.querySelector('.threadly-ai-sidebar.levyo-maker-sidebar-portal') || maker.querySelector('.threadly-ai-sidebar');
      if (sidebar !== observedSidebar && sidebarObserver) {
        sidebarObserver.disconnect();
        observedSidebar = sidebar;
        if (observedSidebar) {
          sidebarObserver.observe(observedSidebar, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'hidden'],
          });
        }
      }
      return sidebar;
    };
    const syncStableAccountGeometry = () => {
      accountGeometryFrame = 0;
      if (disposed) return;
      syncStableSidebarAccountGates(sidebar, stableAccountsVisible);
    };
    const trackStableAccountGeometry = () => {
      window.cancelAnimationFrame?.(accountGeometryFrame);
      accountGeometryFramesRemaining = 24;
      const tick = () => {
        syncStableAccountGeometry();
        accountGeometryFramesRemaining -= 1;
        if (disposed || !stableAccountsVisible || accountGeometryFramesRemaining <= 0) return;
        accountGeometryFrame = window.requestAnimationFrame(tick);
      };
      accountGeometryFrame = window.requestAnimationFrame(tick);
    };
    const captureReturnFocus = () => {
      if (interactionOpen) return;
      const active = document.activeElement;
      returnFocus = active && !sidebar?.contains(active) ? active : null;
    };
    const armHistory = () => {
      if (historyArmed || !window.history?.pushState) return;
      try {
        window.history.pushState(
          Object.assign({}, window.history.state || {}, { threadlySidebarOpen: true }),
          '',
          window.location.href
        );
        historyArmed = true;
      } catch (_error) {}
    };
    const restoreFocus = () => {
      const target = returnFocus;
      returnFocus = null;
      if (!target || !document.contains(target)) return;
      window.requestAnimationFrame(() => target.focus?.({ preventScroll: true }));
    };
    const syncInteractionState = () => {
      const isOpening = maker.classList.contains('is-sidebar-opening');
      const isOpen = maker.classList.contains('is-sidebar-open');
      const isColdOpen = maker.classList.contains('is-levyo-cold-sidebar-open');
      const isColdTransfer = maker.getAttribute('data-threadly-cold-sidebar-transfer') === 'true';
      const isClosing = maker.classList.contains('is-sidebar-closing');
      const next = (isOpening || isOpen || isColdOpen || isColdTransfer) && !isClosing;
      if (next && !interactionOpen) {
        captureReturnFocus();
        armHistory();
      } else if (!next && interactionOpen) {
        if (historyArmed) {
          historyArmed = false;
          try { window.history.back(); } catch (_error) {}
        }
        restorePending = true;
      }
      if (!isClosing && restorePending) {
        restorePending = false;
        restoreFocus();
      }
      interactionOpen = next;
    };
    const handleSidebarKeydown = (event) => {
      if (!interactionOpen || !sidebar || sidebar.inert) return;
      if (event.key === 'Escape' && !window.THREADLY_AI_MAKER_RUNTIME_LOADED && maker.classList.contains('is-levyo-cold-sidebar-open')) {
        event.preventDefault();
        closeColdGuestSidebar(maker);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = sidebarFocusableElements(sidebar);
      if (!focusable.length) {
        event.preventDefault();
        sidebar.focus?.({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const stableAccountActive = active?.matches?.(
        'body > shopify-account[data-threadly-stable-sidebar-account][data-levyo-sidebar-account-active="true"]'
      );
      if (!sidebar.contains(active) && !stableAccountActive) {
        event.preventDefault();
        first.focus?.({ preventScroll: true });
        return;
      }
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus?.({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus?.({ preventScroll: true });
      }
    };
    const handleSidebarPopstate = () => {
      if (!interactionOpen || !historyArmed) return;
      historyArmed = false;
      requestSidebarAction('close', maker);
    };
    const handleSidebarToggleClick = (event) => {
      const toggle = event.target?.closest?.('[data-threadly-sidebar-toggle]');
      if (!toggle || !maker.contains(toggle)) return;
      const visiblyOpen = maker.classList.contains('is-sidebar-open') || maker.classList.contains('is-sidebar-opening');
      if (!visiblyOpen) {
        captureReturnFocus();
        armHistory();
      }
    };
    const handleSidebarOpenIntent = (event) => {
      if (event?.detail?.maker !== maker) return;
      captureReturnFocus();
      armHistory();
    };
    const handleStableAccountOpen = (event) => {
      const account = event.target;
      if (!account?.matches?.(
        'body > shopify-account[data-threadly-stable-sidebar-account][data-levyo-sidebar-account-active="true"]'
      )) return;
      account.classList.add('is-levyo-account-sheet-open');
    };
    const handleStableAccountClose = (event) => {
      const account = event.target;
      if (!account?.matches?.('body > shopify-account[data-threadly-stable-sidebar-account]')) return;
      account.classList.remove('is-levyo-account-sheet-open');
      window.requestAnimationFrame(() => {
        if (!disposed) sync();
      });
    };
    const handlePagehide = (event) => {
      if (!event?.persisted) dispose();
    };
    const handlePageshow = (event) => {
      if (!event?.persisted || disposed) return;
      observer.observe(maker, { attributes: true, attributeFilter: ['class'] });
      sync();
    };
    document.addEventListener('keydown', handleSidebarKeydown);
    window.addEventListener('popstate', handleSidebarPopstate);
    document.addEventListener('click', handleSidebarToggleClick, true);
    document.addEventListener('threadly:levyo-sidebar-open-intent', handleSidebarOpenIntent);
    document.addEventListener('open', handleStableAccountOpen, true);
    document.addEventListener('close', handleStableAccountClose, true);
    const sync = () => {
      if (disposed) return;
      resolveSidebar();
      syncInteractionState();
      const isOpen = maker.classList.contains('is-sidebar-open');
      const isColdOpen = maker.classList.contains('is-levyo-cold-sidebar-open');
      const isClosing = maker.classList.contains('is-sidebar-closing');
      const isVisiblyOpen = isOpen && !isClosing;
      const isColdVisiblyOpen = isColdOpen && !isClosing;
      const isSidebarVisible = isVisiblyOpen || isColdVisiblyOpen;
      const stableAccountVisibilityChanged = stableAccountsVisible !== isSidebarVisible;
      stableAccountsVisible = isSidebarVisible;
      root.classList.toggle('levyo-maker-sidebar-open', isSidebarVisible);
      const portal = document.querySelector('.levyo-maker-sidebar-portal');
      window.clearTimeout(closeTimer);
      if (isVisiblyOpen) {
        portal?.classList.remove('is-levyo-portal-closing');
        portal?.classList.add('is-levyo-portal-open');
      } else if (isColdVisiblyOpen) {
        portal?.classList.remove('is-levyo-portal-closing');
        portal?.classList.add('is-levyo-portal-open');
      } else if (portal?.classList.contains('is-levyo-portal-open')) {
        portal.classList.add('is-levyo-portal-closing');
        portal.classList.remove('is-levyo-portal-open');
        closeTimer = window.setTimeout(() => portal.classList.remove('is-levyo-portal-closing'), 320);
      }
      syncStableSidebarAccountGates(sidebar, isSidebarVisible);
      if (isSidebarVisible && stableAccountVisibilityChanged) trackStableAccountGeometry();
      else if (!isSidebarVisible) window.cancelAnimationFrame?.(accountGeometryFrame);
      const backdrop = ensureSidebarBackdrop();
      backdrop.classList.toggle('is-open', isSidebarVisible);
      backdrop.setAttribute('aria-hidden', isSidebarVisible ? 'false' : 'true');
    };

    const observer = new MutationObserver(sync);
    sidebarObserver = new MutationObserver(() => {
      if (accountGeometryFrame || disposed) return;
      accountGeometryFrame = window.requestAnimationFrame(syncStableAccountGeometry);
    });
    observer.observe(maker, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('pagehide', handlePagehide);
    window.addEventListener('pageshow', handlePageshow, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    const binding = {
      maker,
      sync,
      dispose() {
        if (disposed) return;
        disposed = true;
        observer.disconnect();
        sidebarObserver.disconnect();
        window.clearTimeout(closeTimer);
        window.cancelAnimationFrame?.(accountGeometryFrame);
        stableAccountsVisible = false;
        syncStableSidebarAccountGates(null, false);
        document.removeEventListener('keydown', handleSidebarKeydown);
        window.removeEventListener('popstate', handleSidebarPopstate);
        document.removeEventListener('click', handleSidebarToggleClick, true);
        document.removeEventListener('threadly:levyo-sidebar-open-intent', handleSidebarOpenIntent);
        document.removeEventListener('open', handleStableAccountOpen, true);
        document.removeEventListener('close', handleStableAccountClose, true);
        window.removeEventListener('pagehide', handlePagehide);
        window.removeEventListener('pageshow', handlePageshow);
        window.removeEventListener('resize', sync);
        if (historyArmed) {
          historyArmed = false;
          try { window.history.back(); } catch (_error) {}
        }
        returnFocus = null;
        restorePending = false;
        const oldSidebar = sidebar;
        if (oldSidebar?.parentElement === document.body && oldSidebar.classList.contains('levyo-maker-sidebar-portal')) {
          oldSidebar.remove();
        }
        if (activeSidebarBinding?.maker === maker) activeSidebarBinding = null;
      }
    };
    activeSidebarBinding = binding;
    sync();
    return binding;
  };

  const initialize = () => {
    const maker = document.querySelector('#threadly-ai-panel.threadly-ai-maker');
    if (!maker) return false;

    if (activeSidebarBinding?.maker !== maker) activeSidebarBinding?.dispose();
    if (coldGuestSidebar?.parentElement !== maker) disposeColdGuestSidebar();
    if (window.THREADLY_AI_MAKER_RUNTIME_LOADED) disposeColdGuestSidebar();
    connectHeaderMenu();
    arrangeMakerHeader(maker);
    const sidebarReady = enhanceSidebar(maker);
    ensureSidebarBackdrop();
    syncSidebarViewportState(maker);
    if (sidebarReady && pendingSidebarToggle) {
      maker.setAttribute('data-threadly-cold-sidebar-transfer', 'true');
      maker.classList.remove('is-levyo-cold-sidebar-open');
      if (requestSidebarAction('toggle', maker)) pendingSidebarToggle = false;
      maker.removeAttribute('data-threadly-cold-sidebar-transfer');
    }
    if (sidebarReady) openRequestedView(maker.closest('#threadly-konva-designer'));
    return sidebarReady;
  };

  if (!initialize()) {
    const observer = new MutationObserver(() => {
      if (!initialize()) return;
      observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  document.addEventListener('threadly:maker-runtime-ready', initialize, { passive: true });
  window.addEventListener('pageshow', (event) => {
    if (event?.persisted) initialize();
  }, { passive: true });
})();
